/**
 * 業務日と時刻の相互変換。
 *
 * ADM-04 / ADM-05 / ADM-27。画面は日本時間の「業務日」で操作し、APIはUTCの
 * ISO文字列を受け取る。この2つを各画面が個別に変換していたため、
 * `toISOString().slice(0,10)` による日付のずれ、`+9h` の二重加算、
 * 未退勤（null）に `Z` を結合する変換エラーが混在していた。
 *
 * 変換はここだけで行い、画面はこの関数を呼ぶ。
 *
 * EV-116 段階2。業務日は会社の時間帯（プロフィールの company_info.time_zone）の
 * [00:00, 翌日00:00)。夏時間のある時間帯も、時間帯の名前（Intl）で計算する。
 */

/** 既定の業務時間帯。会社の時間帯が読めないとき・会社に属さない利用者に使う。 */
export const DEFAULT_BUSINESS_TIME_ZONE = 'Asia/Tokyo';

/**
 * 今の業務時間帯（会社の時間帯。IANA の名前）。
 *
 * EV-116 段階2。段階1までは Asia/Tokyo に固定し、+9時間の足し引きで計算していた。
 * 会社の時間帯はプロフィールの company_info.time_zone で、レイアウト（CompanyTimeZoneProvider）が
 * setBusinessTimeZone で渡す。時刻（UTC）を業務時間帯の年月日・時分へ、また業務時間帯の年月日・
 * 時分を UTC の時刻へ、時間帯の名前（Intl）で変換する。1日の長さを24時間と決めつけない。
 */
let businessTimeZone = DEFAULT_BUSINESS_TIME_ZONE;

/** 今の業務時間帯の名前。 */
export function getBusinessTimeZone(): string {
  return businessTimeZone;
}

/** ブラウザの Intl.DateTimeFormat が受け付ける時間帯の名前か。 */
export function isUsableTimeZone(zone: unknown): zone is string {
  if (typeof zone !== 'string' || zone === '') return false;
  try {
    partsFormatter(zone);
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * 業務時間帯を変える。使えない名前（空・ブラウザが知らない名前）は採らずに既定へ戻し、false を返す。
 * 変えた後に描いた画面から新しい時間帯で計算する（描き直しは CompanyTimeZoneProvider）。
 */
export function setBusinessTimeZone(zone: string | null | undefined): boolean {
  if (isUsableTimeZone(zone)) {
    businessTimeZone = zone;
    return true;
  }
  businessTimeZone = DEFAULT_BUSINESS_TIME_ZONE;
  return false;
}

/**
 * サーバーで日・月に分ける API（summarize_monthly・recent_submission）に付ける `time_zone=…&`。
 * いつも、画面が境界の計算に使っている今の業務時間帯（実効の時間帯）を渡す。
 *
 * RV-278・RV-279（PR #216 の自動レビュー）。サーバーは会社の時間帯で分けるので、画面が別の時間帯で
 * 計算していると、月の境目の記録が別の月に入り得た。ブラウザが会社の時間帯を知らないとき・最初の
 * プロフィールの取得に失敗して既定の Asia/Tokyo で描くとき・読み直しの途中など、どの経路でも
 * 画面とサーバーの分け方をそろえるため、「代わりの時間帯かどうか」の判定に頼らずに渡す。
 */
export function businessZoneQuery(): string {
  return `time_zone=${encodeURIComponent(businessTimeZone)}&`;
}

/** Date を業務時間帯の `YYYY-MM-DD` にする。UTC日付へ丸めない。 */
export function toBusinessDate(value: Date | string | null | undefined): string {
  const date = toDate(value);
  if (!date) return '';
  const p = zonedParts(date.getTime());
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/**
 * 業務日の開始時刻（会社の時間帯の 00:00）をUTCのISO文字列で返す。
 *
 * EV-116 段階2。00:00 が無い日（夏時間の切り替えが0時にある地域）は、その日の最初の時刻
 * （例 01:00）。Python の zoneinfo の fold=0 と同じ結果。
 */
export function businessDayStart(businessDate: string): string {
  return new Date(dayStartMillis(businessDate)).toISOString();
}

/**
 * 業務日の終了時刻（翌日の00:00 の1マイクロ秒前）をUTCのISO文字列で返す。
 *
 * RV-007。API は期間を gte / lte（両端を含む）で受ける。DB の時刻の精度はマイクロ秒なので、
 * 終わりを翌日00:00 の 1マイクロ秒前（…59.999999）にすると [00:00, 翌日00:00) と同じ範囲になる。
 * 以前は 23:59:59.999（Date の精度）で、最後の1ミリ秒未満の記録が外れていた。
 * EV-116 段階2。終わりは「その日の00:00 ＋24時間」ではなく、翌日の00:00 から求める
 * （夏時間の切り替えの日は23時間・25時間）。
 */
export function businessDayEnd(businessDate: string): string {
  const next = dayStartMillis(nextCalendarDate(businessDate));
  return new Date(next - 1).toISOString().replace(/Z$/, '999Z');
}

/** 暦日の翌日（`YYYY-MM-DD`）。時間帯に依存しない（日付の文字列のまま計算する）。 */
export function nextCalendarDate(businessDate: string): string {
  const [year, month, day] = businessDate.split('-').map(Number);
  const next = new Date(Date.UTC(year, (month || 1) - 1, (day || 1) + 1));
  return next.toISOString().slice(0, 10);
}

/**
 * 暦の月の初日と末日（`YYYY-MM-DD`）。month は 1〜12。時間帯に依存しない。
 *
 * EV-116 段階2。`toBusinessDate(new Date(Date.UTC(y, m, d)))` で暦日を作る書き方は、UTC より
 * 西の時間帯（LA など）で前の日になっていた。
 */
export function calendarMonthBounds(year: number, month: number): { start: string; end: string } {
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return {
    start: `${year}-${pad(month)}-01`,
    end: `${year}-${pad(month)}-${pad(lastDay)}`,
  };
}

/**
 * 基準時刻（既定は現在）を含む、業務時間帯の月の初日と末日（`YYYY-MM-DD`）。
 *
 * ADM-05。端末の時間帯で月を決めると、日本時間の月初0〜9時に前月を数えていた。
 */
export function businessMonthRange(now: Date = new Date()): { start: string; end: string } {
  const [year, month] = toBusinessDate(now).split('-').map(Number);
  return calendarMonthBounds(year, month);
}

/** 期間フィルタの gte / lte を業務日から作る。空文字は指定なしとして扱う。 */
export function businessDateRange(
  from: string | null | undefined,
  to: string | null | undefined
): { gte?: string; lte?: string } {
  const range: { gte?: string; lte?: string } = {};
  if (from) range.gte = businessDayStart(from);
  if (to) range.lte = businessDayEnd(to);
  return range;
}

/**
 * 期間フィルタを `項目__gte=…&項目__lte=…&` の形で返す。値はURLエンコード済み。
 * 画面で UTC の 0時・24時を直接書かず、必ずこれか businessDateRange を使う。
 */
export function businessRangeQuery(
  field: string,
  from: string | null | undefined,
  to: string | null | undefined
): string {
  const { gte, lte } = businessDateRange(from, to);
  let query = '';
  if (gte) query += `${field}__gte=${encodeURIComponent(gte)}&`;
  if (lte) query += `${field}__lte=${encodeURIComponent(lte)}&`;
  return query;
}

/**
 * 画面の日付入力・時刻入力からUTCのISO文字列を作る。
 *
 * 時刻が空の場合は null を返す。未退勤を「日付だけ」で送って
 * 不正な時刻にしないため、呼び出し側でnullを判定できるようにする。
 */
export function businessDateTimeToIso(
  businessDate: string | null | undefined,
  time: string | null | undefined
): string | null {
  if (!businessDate || !time) return null;
  // EV-116 段階2（2026-10-11 PM 決定7）。2回ある時刻は先の方（fold=0）。存在しない時刻は
  // 画面で断る（businessLocalTimeStatus）。ここでは Python の zoneinfo の fold=0 と同じく、
  // 切り替えの前の差で読む。
  return new Date(localToUtc(businessDate, time).millis).toISOString();
}

/**
 * 業務時間帯の日付・時刻が、夏時間の切り替えで存在しない（nonexistent）か、2回ある（ambiguous）か。
 * EV-116 段階2（2026-10-11 PM 決定7）。編集の画面が、存在しない時刻を断り、2回ある時刻に案内を出す。
 */
export function businessLocalTimeStatus(
  businessDate: string | null | undefined,
  time: string | null | undefined
): 'ok' | 'nonexistent' | 'ambiguous' {
  if (!businessDate || !time) return 'ok';
  return localToUtc(businessDate, time).status;
}

/**
 * 編集欄の日付・時刻からUTCのISO文字列を作る。元の値から変えていなければ元の値をそのまま返す。
 *
 * ADM-04。編集欄は「時:分」までしか持たないため、組み立て直すと秒・ミリ秒が0になり、
 * 何も変えずに保存しただけで記録の時刻が変わっていた。
 */
export function businessDateTimeToIsoKeeping(
  businessDate: string | null | undefined,
  time: string | null | undefined,
  original: string | null | undefined
): string | null {
  if (
    original &&
    toDate(original) &&
    businessDate === isoToBusinessDate(original) &&
    (time ?? '').slice(0, 5) === isoToBusinessTime(original)
  ) {
    return original;
  }
  return businessDateTimeToIso(businessDate, time);
}

/** UTCのISO文字列を、画面の日付入力用 `YYYY-MM-DD` にする。 */
export function isoToBusinessDate(value: string | null | undefined): string {
  return toBusinessDate(value);
}

/** UTCのISO文字列を、画面の時刻入力用 `HH:mm` にする。空はそのまま空。 */
export function isoToBusinessTime(value: string | null | undefined): string {
  const date = toDate(value);
  if (!date) return '';
  const p = zonedParts(date.getTime());
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

/** UTCのISO文字列を、業務時間帯の `HH:mm:ss` にする（CSV 用）。空・不正な値は空文字。 */
export function isoToBusinessClock(value: string | null | undefined): string {
  const date = toDate(value);
  if (!date) return '';
  const p = zonedParts(date.getTime());
  return `${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`;
}

/** その時刻の業務時間帯の UTC との差（分。東が正。例 Asia/Tokyo は 540）。不正な値は今の時刻で。 */
export function businessUtcOffsetMinutes(value: Date | string | null | undefined = new Date()): number {
  const date = toDate(value) ?? new Date();
  return offsetMinutesAt(date.getTime(), businessTimeZone);
}

/** UTC との差（分）を `UTC+09:00`・`UTC-07:00` の形にする。 */
export function formatUtcOffset(minutes: number): string {
  return `UTC${signedOffset(minutes, ':')}`;
}

/**
 * 端末の現地の時刻（EV-116 段階3）。記録の UTC の時刻に端末の UTC との差（分）を足し、`MM/dd HH:mm` にする。
 * 基準（会社の時間帯）と差が違う行の補足に使う。空・不正は空文字。
 */
export function formatDeviceLocalClock(value: Date | string | null | undefined, offsetMinutes: number): string {
  const date = toDate(value);
  if (!date || !Number.isFinite(offsetMinutes)) return '';
  const shifted = new Date(date.getTime() + offsetMinutes * 60000);
  return `${pad(shifted.getUTCMonth() + 1)}/${pad(shifted.getUTCDate())} ${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}`;
}

/** UTC との差（分）を `+0900`・`-0700` の形にする（ファイル名用）。 */
export function compactUtcOffset(minutes: number): string {
  return signedOffset(minutes, '');
}

/**
 * from より後で、業務時間帯の UTC との差が次に変わる時刻（夏時間に入る・抜ける）。400日の間に変わらなければ null。
 * RV-280。開いたままの画面で、集計の基準の差を切り替わりの時刻に描き直すために使う。
 * 1日ずつ進めて差が変わる日を見つけ、その中を1秒まで二分して求める（切り替わりは秒の境目）。
 */
export function nextUtcOffsetChange(from: Date = new Date(), zone: string = businessTimeZone): Date | null {
  const start = Math.floor(from.getTime() / 1000) * 1000;
  const base = offsetMinutesAt(start, zone);
  let low = start;
  for (let day = 1; day <= 400; day += 1) {
    let high = start + day * DAY_MILLIS;
    if (offsetMinutesAt(high, zone) !== base) {
      while (high - low > 1000) {
        const middle = Math.floor((low + high) / 2000) * 1000;
        if (offsetMinutesAt(middle, zone) === base) low = middle;
        else high = middle;
      }
      return new Date(high);
    }
    low = high;
  }
  return null;
}

/** 時間帯の名前と、その時点の UTC との差（`Asia/Tokyo (UTC+09:00)`）。既定は今の業務時間帯・今。 */
export function businessZoneLabel(
  zone: string = businessTimeZone,
  at: Date = new Date()
): string {
  return `${zone} (${formatUtcOffset(offsetMinutesAt(at.getTime(), zone))})`;
}

/** 表示用。null / 空文字は表示上の欠落として扱い、`Invalid Date` を出さない。 */
export function formatBusinessDateTime(
  value: string | null | undefined,
  fallback = '—'
): string {
  const date = toDate(value);
  if (!date) return fallback;
  return `${toBusinessDate(date)} ${isoToBusinessTime(date.toISOString())}`;
}

/**
 * 日時を業務時間帯で、date-fns と同じ書式の記号（`yyyy`・`MM`・`MMM`・`dd`・`HH`・`mm`・`ss`）で文字にする。
 * 例 `yyyy/MM/dd`・`HH:mm`・`dd MMM yyyy | HH:mm`。`MMM` は英語の月の略称（Jan〜Dec）。
 * 空・不正な値は fallback を返す。ほかの英字は date-fns と同じく誤りにする（'…' で囲めば文字のまま）。
 *
 * EV-116。表の日付・時刻を端末の時間帯（getDate()・toLocaleTimeString() など）で作っていたため、
 * 日本以外の時間帯のパソコンでは、業務日で絞った範囲と表示の日付が食い違っていた。
 * 業務時間帯の年月日・時分は Intl の timeZone で取り出す。date-fns-tz の formatInTimeZone は
 * 一度「端末の時間帯の Date」に置き換えるため、端末の夏時間で存在しない時刻に当たると1時間進んだ
 * （独立照合 2026-10-11）。
 */
export function formatBusiness(
  value: Date | string | null | undefined,
  pattern: string,
  fallback = ''
): string {
  const date = toDate(value);
  if (!date) return fallback;
  const p = zonedParts(date.getTime());
  const values: Record<string, string> = {
    yyyy: String(p.year).padStart(4, '0'),
    MMM: MONTH_ABBREVIATIONS[p.month - 1],
    MM: pad(p.month),
    dd: pad(p.day),
    HH: pad(p.hour),
    mm: pad(p.minute),
    ss: pad(p.second),
  };
  return pattern.replace(/yyyy|MMM|MM|dd|HH|mm|ss|'[^']*'|[A-Za-z]/g, (token) => {
    if (token in values) return values[token];
    if (token.startsWith("'")) return token.slice(1, -1);
    throw new RangeError(`formatBusiness: 使えない書式の文字 \`${token}\``);
  });
}

/** 時間帯ごとの Intl.DateTimeFormat（年月日・時分秒を数字で取り出す）。作るのが重いので使い回す。 */
const PARTS_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(zone: string): Intl.DateTimeFormat {
  let formatter = PARTS_FORMATTERS.get(zone);
  if (!formatter) {
    // 使えない名前は RangeError（isUsableTimeZone が受ける）。
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    PARTS_FORMATTERS.set(zone, formatter);
  }
  return formatter;
}

type ZonedParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

/** UTC のミリ秒を、時間帯の年月日・時分秒にする（端末の時間帯の Date を作らない）。 */
function zonedParts(millis: number, zone: string = businessTimeZone): ZonedParts {
  const parts: Record<string, string> = {};
  for (const part of partsFormatter(zone).formatToParts(new Date(millis))) {
    parts[part.type] = part.value;
  }
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    // 古い実装は 0時を 24 と出すことがある（hourCycle h23 を無視する場合）。
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

/** その時刻の、時間帯の UTC との差（分。東が正）。 */
function offsetMinutesAt(millis: number, zone: string): number {
  const whole = Math.floor(millis / 1000) * 1000;
  const p = zonedParts(whole, zone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - whole) / 60_000);
}

const DAY_MILLIS = 24 * 60 * 60 * 1000;

/**
 * 業務時間帯の日付・時刻（壁の時計）を UTC のミリ秒にする。
 *
 * 前後の差（36時間前・その時・36時間後）を候補にし、その差で戻した時刻の差が一致するものを
 * 正しい読み方とする。0個なら存在しない時刻（切り替えで飛ぶ）、2個なら2回ある時刻。
 * - 2回ある時刻は先の方（UTC で早い方。fold=0）。
 * - 存在しない時刻は、切り替えの前の差で読む（Python の zoneinfo の fold=0 と同じ。例 LA の
 *   3/8 02:30 は 03:30 PDT、チリの 9/6 00:00 は 01:00）。日の始まりはこれで「その日の最初の時刻」になる。
 */
function localToUtc(
  businessDate: string,
  time: string
): { millis: number; status: 'ok' | 'nonexistent' | 'ambiguous' } {
  const [year, month, day] = businessDate.split('-').map(Number);
  const [hour, minute, second] = time.split(':').map((part) => Number(part) || 0);
  const wall = Date.UTC(year, (month || 1) - 1, day || 1, hour, minute, second || 0, 0);
  const zone = businessTimeZone;
  const before = offsetMinutesAt(wall - 1.5 * DAY_MILLIS, zone);
  const candidates = Array.from(
    new Set([before, offsetMinutesAt(wall, zone), offsetMinutesAt(wall + 1.5 * DAY_MILLIS, zone)])
  );
  const valid = candidates
    .map((offset) => wall - offset * 60_000)
    .filter((millis) => offsetMinutesAt(millis, zone) * 60_000 === wall - millis)
    .sort((a, b) => a - b);
  if (valid.length === 0) return { millis: wall - before * 60_000, status: 'nonexistent' };
  return { millis: valid[0], status: valid.length > 1 ? 'ambiguous' : 'ok' };
}

/** 業務日の 00:00（無ければその日の最初の時刻）の UTC のミリ秒。 */
function dayStartMillis(businessDate: string): number {
  return localToUtc(businessDate, '00:00').millis;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function signedOffset(minutes: number, separator: string): string {
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  return `${sign}${pad(Math.floor(abs / 60))}${separator}${pad(abs % 60)}`;
}

const MONTH_ABBREVIATIONS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** 2つの時刻の差を時間で返す。終了が無い場合は基準時刻（既定は現在）まで。 */
export function durationHours(
  start: string | null | undefined,
  stop: string | null | undefined,
  now: Date = new Date()
): number | null {
  const from = toDate(start);
  if (!from) return null;
  const to = toDate(stop) ?? now;
  const hours = (to.getTime() - from.getTime()) / 3_600_000;
  return hours < 0 ? 0 : hours;
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}
